import {
  useCallback,
  useState,
  useSyncExternalStore,
  type DragEvent,
  type KeyboardEvent,
} from 'react';
import { GripVertical } from 'lucide-react';
import './field-display-order.css';
import { TooltipButton } from './TooltipButton';

const changed = 'capturepoint-field-display-order';
const dragType = 'application/x-capturepoint-field-order';
export type FieldReorder = {
  key: string;
  id: string;
  name: string;
  move: (id: string, target: string, after?: boolean) => void;
  step: (id: string, direction: number) => void;
};

export function orderDisplayFields<T extends { id: string }>(
  fields: readonly T[],
  saved: unknown,
): T[] {
  const ids = Array.isArray(saved)
    ? saved.filter((id): id is string => typeof id === 'string')
    : [];
  const byId = new Map(fields.map((field) => [field.id, field]));
  const result: T[] = [];
  for (const id of ids) {
    const field = byId.get(id);
    if (field) {
      result.push(field);
      byId.delete(id);
    }
  }
  return [...result, ...byId.values()];
}

/** Workstation display preference only; never writes job schemas or learning. */
export function useFieldDisplayOrder<T extends { id: string; name: string }>(
  jobId: string,
  typeId: string,
  fields: readonly T[],
) {
  const key = `cpv6-field-display-order:${JSON.stringify([jobId, typeId])}`;
  const [error, setError] = useState('');
  const subscribe = useCallback((notify: () => void) => {
    const listener = () => notify();
    window.addEventListener('storage', listener);
    window.addEventListener(changed, listener);
    return () => {
      window.removeEventListener('storage', listener);
      window.removeEventListener(changed, listener);
    };
  }, []);
  const read = useCallback(() => {
    try {
      return localStorage.getItem(key) ?? '';
    } catch {
      return '';
    }
  }, [key]);
  const raw = useSyncExternalStore(subscribe, read);
  let saved: unknown;
  try {
    saved = JSON.parse(raw);
  } catch {
    saved = [];
  }
  const ordered = orderDisplayFields(fields, saved);
  const write = (ids: string[]) => {
    if (!jobId || !typeId) {
      setError('Open a configured document type before arranging its fields.');
      return;
    }
    try {
      localStorage.setItem(key, JSON.stringify(ids));
      setError('');
      window.dispatchEvent(new Event(changed));
    } catch {
      setError('The field order could not be saved. Try again.');
    }
  };
  const move = (id: string, target: string, after = false) => {
    if (
      id === target ||
      !ordered.some((field) => field.id === id) ||
      !ordered.some((field) => field.id === target)
    )
      return;
    const ids = ordered.map((field) => field.id).filter((value) => value !== id);
    ids.splice(ids.indexOf(target) + (after ? 1 : 0), 0, id);
    write(ids);
  };
  const step = (id: string, direction: number) => {
    const ids = ordered.map((field) => field.id),
      index = ids.indexOf(id),
      next = index + direction;
    if (index < 0 || next < 0 || next >= ids.length) return;
    [ids[index], ids[next]] = [ids[next], ids[index]];
    write(ids);
  };
  return {
    fields: ordered,
    error,
    reorder: (field: T): FieldReorder => ({ key, id: field.id, name: field.name, move, step }),
  };
}

export function FieldOrderHandle({ order }: { order: FieldReorder }) {
  const keydown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.altKey && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
      event.preventDefault();
      event.stopPropagation();
      order.step(order.id, event.key === 'ArrowUp' ? -1 : 1);
    }
  };
  return (
    <TooltipButton
      type="button"
      className="field-order-handle"
      draggable
      aria-label={`Move ${order.name}. Drag, or use Alt plus Up or Down.`}
      tooltip="Drag to reorder · Alt + Up/Down"
      onKeyDown={keydown}
      onClick={(event) => event.preventDefault()}
      onDragStart={(event) => {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData(dragType, JSON.stringify({ key: order.key, id: order.id }));
      }}
    >
      <GripVertical size={14} />
    </TooltipButton>
  );
}

export function fieldOrderDrop(event: DragEvent, order: FieldReorder) {
  event.currentTarget.classList.remove('field-order-target', 'field-order-after');
  if (!event.dataTransfer.types.includes(dragType)) return;
  event.preventDefault();
  try {
    const value = JSON.parse(event.dataTransfer.getData(dragType));
    const rect = event.currentTarget.getBoundingClientRect();
    if (value.key === order.key && typeof value.id === 'string')
      order.move(value.id, order.id, event.clientY >= rect.top + rect.height / 2);
  } catch {
    /* Ignore unrelated/malformed drags. */
  }
}
export function fieldOrderOver(event: DragEvent) {
  if (!event.dataTransfer.types.includes(dragType)) return;
  event.preventDefault();
  event.dataTransfer.dropEffect = 'move';
  event.currentTarget.classList.add('field-order-target');
  const rect = event.currentTarget.getBoundingClientRect();
  event.currentTarget.classList.toggle(
    'field-order-after',
    event.clientY >= rect.top + rect.height / 2,
  );
}
